import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Btn, Card } from "@/components/cc";
import { ArrowLeft, Lock, Network, ShieldAlert, ListTree, Share2, Users, Clock, CheckSquare } from "lucide-react";

const pretty = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Editorial verification is encoded by an accessible LINE PATTERN (not colour alone) plus a text label.
const EDITORIAL_DASH = { verified: "", partially_verified: "7 4", unverified: "2 4", disputed: "9 3 2 3", corrected_false: "1 5" };
const EDITORIAL_WORD = { verified: "solid line", partially_verified: "long-dashed line", unverified: "dotted line", disputed: "dash-dot line", corrected_false: "fine-dotted line" };

// Three-dimension badge group. Uses icon + text (never colour alone) so it is screen-reader accessible.
function DimBadges({ edge }) {
  if (edge.access_restricted) {
    return <span data-testid="cult-graph-edge-restricted" className="inline-flex items-center gap-1 text-[0.6rem] px-1.5 py-0.5 rounded-sm border border-red-500/40 text-red-300"><Lock className="h-3 w-3" /> Restricted relationship — details withheld</span>;
  }
  return (
    <span className="inline-flex flex-wrap gap-1">
      <span className="inline-flex items-center gap-1 text-[0.6rem] px-1.5 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-300" title={`Editorial verification: ${pretty(edge.editorial_verification)} (${EDITORIAL_WORD[edge.editorial_verification] || "line"})`}><CheckSquare className="h-3 w-3" /> Editorial: {pretty(edge.editorial_verification)}</span>
      <span className="inline-flex items-center gap-1 text-[0.6rem] px-1.5 py-0.5 rounded-sm border border-fuchsia-500/40 text-fuchsia-300" title={`Community validation: ${pretty(edge.community_validation)}`}><Users className="h-3 w-3" /> Community: {pretty(edge.community_validation)}</span>
      <span className="inline-flex items-center gap-1 text-[0.6rem] px-1.5 py-0.5 rounded-sm border border-sky-500/40 text-sky-300" title={`Historical certainty: ${pretty(edge.historical_certainty) || "not recorded"}`}><Clock className="h-3 w-3" /> Historical: {pretty(edge.historical_certainty) || "Not recorded"}</span>
    </span>
  );
}

export default function CultureGraph() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const recordId = sp.get("record") || "";
  const entityId = sp.get("entity") || "";
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("visual"); // visual | text

  useEffect(() => {
    const params = {};
    if (recordId) params.record_id = recordId;
    if (entityId) params.entity_id = entityId;
    api.get("/culture/graph", { params }).then((r) => setData(r.data)).catch(() => setData({ nodes: [], edges: [], text_alternative: [] }));
  }, [recordId, entityId]);

  // simple radial layout for the visual SVG
  const layout = useMemo(() => {
    if (!data) return {};
    const cx = 300, cy = 230, R = 170;
    const pos = {};
    data.nodes.forEach((n, i) => {
      const a = (2 * Math.PI * i) / Math.max(1, data.nodes.length) - Math.PI / 2;
      pos[n.id] = { x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) };
    });
    return pos;
  }, [data]);

  if (!data) return <div className="p-8 text-zinc-500">Loading graph…</div>;
  const back = recordId ? `/command-center/culture/${recordId}` : entityId ? `/command-center/entities/${entityId}` : "/command-center/culture";

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-6xl" data-testid="cult-graph-page">
      <button onClick={() => nav(back)} className="text-xs text-zinc-500 hover:text-white flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
      <PageHeader title="Culture Relationship Graph" subtitle="Private · communities, clans, lineages, languages, traditional states, artifacts, festivals, heritage sites & diaspora — three independent evaluation dimensions shown separately">
        <span className="text-xs px-2 py-1 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> Private</span>
        <div className="flex rounded-sm border border-zinc-800 overflow-hidden">
          <button data-testid="cult-graph-mode-visual" onClick={() => setMode("visual")} className={`px-3 py-1.5 text-xs flex items-center gap-1 ${mode === "visual" ? "bg-zinc-800 text-white" : "text-zinc-400"}`}><Share2 className="h-3.5 w-3.5" /> Graph</button>
          <button data-testid="cult-graph-mode-text" onClick={() => setMode("text")} className={`px-3 py-1.5 text-xs flex items-center gap-1 ${mode === "text" ? "bg-zinc-800 text-white" : "text-zinc-400"}`}><ListTree className="h-3.5 w-3.5" /> Text alternative</button>
        </div>
      </PageHeader>

      {/* Legend — the three independent dimensions, explained without relying on colour */}
      <Card className="p-3 text-[0.65rem] text-zinc-400" data-testid="cult-graph-legend">
        <div className="grid md:grid-cols-3 gap-2">
          <div><span className="text-emerald-300 font-medium">Editorial verification</span> — shown as a line pattern + label (solid = verified, long-dash = partially, dotted = unverified, dash-dot = disputed).</div>
          <div><span className="text-fuchsia-300 font-medium">Community validation</span> — shown as a separate labelled badge with a people icon.</div>
          <div><span className="text-sky-300 font-medium">Historical certainty</span> — shown as a separate labelled badge with a clock icon. Never merged into a single score.</div>
        </div>
      </Card>

      {data.nodes.length === 0 && <p className="text-sm text-zinc-600" data-testid="cult-graph-empty">No relationships to display for this view.</p>}

      {mode === "visual" && data.nodes.length > 0 && (
        <Card className="p-3 overflow-x-auto" data-testid="cult-graph-visual">
          <svg viewBox="0 0 600 460" className="w-full max-w-2xl mx-auto" role="img" aria-label="Culture relationship graph. An accessible text alternative is available via the Text alternative tab.">
            <defs>
              <marker id="arrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto"><path d="M0,0 L7,3 L0,6 Z" fill="#71717a" /></marker>
            </defs>
            {data.edges.map((e, i) => {
              const a = layout[e.source], b = layout[e.target];
              if (!a || !b) return null;
              const restricted = e.access_restricted;
              return (
                <g key={`${e.id}-${i}`}>
                  <line x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke={restricted ? "#f87171" : "#a1a1aa"} strokeWidth="1.5"
                    strokeDasharray={restricted ? "3 3" : (EDITORIAL_DASH[e.editorial_verification] || "")}
                    markerEnd="url(#arrow)" />
                  <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 3} fill={restricted ? "#fca5a5" : "#d4d4d8"} fontSize="8" textAnchor="middle">
                    {restricted ? "restricted" : (e.relationship_label || "").slice(0, 22)}
                  </text>
                </g>
              );
            })}
            {data.nodes.map((n) => {
              const p = layout[n.id];
              if (!p) return null;
              const isRec = n.kind === "record";
              return (
                <g key={n.id}>
                  <circle cx={p.x} cy={p.y} r={isRec ? 9 : 6} fill={isRec ? "#f59e0b" : "#38bdf8"} />
                  <text x={p.x} y={p.y - 12} fill="#e4e4e7" fontSize="9" textAnchor="middle">{(n.name || "").slice(0, 20)}</text>
                </g>
              );
            })}
          </svg>
          <p className="text-[0.6rem] text-zinc-600 mt-2">Amber node = originating culture record · blue node = canonical entity. Arrow line pattern encodes editorial verification (see legend). Community & historical dimensions are listed per relationship in the Text alternative.</p>
        </Card>
      )}

      {/* Accessible edge list — always contains the SAME information as the visual graph */}
      <Card className="p-4 space-y-2" data-testid="cult-graph-edges">
        <div className="label-caps text-zinc-500 text-[0.6rem] flex items-center gap-1"><Network className="h-3.5 w-3.5" /> Relationships ({data.edges.length})</div>
        <ul className="space-y-2">
          {data.edges.map((e, i) => (
            <li key={`${e.id}-${i}`} data-testid={`cult-graph-edge-${e.id}`} tabIndex={0}
              className="border border-zinc-800 rounded-sm p-2 focus:outline-none focus:ring-2 focus:ring-amber-500/60 hover:border-zinc-600">
              <div className="text-xs text-zinc-200 flex flex-wrap items-center gap-1.5">
                <button className="text-sky-300 hover:underline" onClick={() => e.source?.startsWith("entity:") && nav(`/command-center/entities/${e.source.slice(7)}`)}>{data.nodes.find((n) => n.id === e.source)?.name || "?"}</button>
                <span className="text-amber-300/90 flex items-center gap-1">{e.access_restricted ? <><Lock className="h-3 w-3" /> restricted</> : (e.relationship_label || pretty(e.relationship_type))}</span>
                <span className="text-zinc-500">→</span>
                <button className="text-sky-300 hover:underline" onClick={() => e.target?.startsWith("entity:") && nav(`/command-center/entities/${e.target.slice(7)}`)}>{data.nodes.find((n) => n.id === e.target)?.name || "?"}</button>
              </div>
              <div className="mt-1"><DimBadges edge={e} /></div>
            </li>
          ))}
          {data.edges.length === 0 && <li className="text-xs text-zinc-600">No relationships.</li>}
        </ul>
      </Card>

      {mode === "text" && (
        <Card className="p-4 space-y-1" data-testid="cult-graph-text">
          <div className="label-caps text-zinc-500 text-[0.6rem]">Screen-reader text alternative</div>
          {data.text_alternative.length === 0 && <p className="text-xs text-zinc-600">No relationships.</p>}
          <ol className="list-decimal list-inside space-y-1 text-xs text-zinc-300">
            {data.text_alternative.map((t, i) => <li key={i} data-testid={`cult-graph-text-${i}`}>{t}</li>)}
          </ol>
        </Card>
      )}
    </div>
  );
}
